import { useState } from "react";
import { Crown, ExternalLink, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/common";
import { PhotoViewer } from "@/components/PhotoViewer";
import { RouteMap } from "@/components/experience/RouteMap";
import { routeOf } from "@/components/experience/RouteEditor";
import { api, apiError, imageUrl } from "@/lib/api";
import { specLine, typeContent } from "@/lib/experienceTypes";

const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return ""; } };

const VoteBar = ({ exp, stay, index, onChange }) => {
  const [busy, setBusy] = useState("");
  const inCircle = ["lead", "cohost"].includes(exp.my_role) && !exp.is_sample;
  const open = !["closed", "cancelled"].includes(exp.status);
  if (!inCircle || !stay.id) return null;
  const mine = exp.my_vote === stay.id;
  const act = async (kind) => {
    setBusy(kind);
    try { const { data } = await api.post(`/experiences/${exp.id}/options/${stay.id}/${kind}`); onChange(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4" data-testid={`stay-${index}-votes`}>
      {open && <button type="button" onClick={() => act("vote")} disabled={!!busy} aria-pressed={mine} data-testid={`stay-${index}-vote-button`}
        className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-4 text-sm transition-colors ${mine ? "border-primary bg-primary/15 text-primary" : "border-border hover:border-primary hover:text-primary"}`}>
        {busy === "vote" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className={`h-4 w-4 ${mine ? "fill-current" : ""}`} />} {mine ? "Your favourite" : "Vote favourite"}
      </button>}
      <span className="text-sm text-muted-foreground" data-testid={`stay-${index}-vote-count`}>{stay.votes.length} vote{stay.votes.length === 1 ? "" : "s"}</span>
      {stay.votes.length > 0 && <span className="flex -space-x-2">{stay.votes.slice(0, 6).map((p) => <span key={p.id} title={p.name}><Avatar person={p} size={30} /></span>)}</span>}
      {exp.my_role === "lead" && open && <button type="button" onClick={() => act("choose")} disabled={!!busy} className="btn-outline ml-auto min-h-[40px] px-4 text-sm" data-testid={`stay-${index}-choose-button`}>
        {busy === "choose" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />} {stay.chosen ? "Unmark chosen" : "Mark as chosen"}</button>}
    </div>
  );
};

const StayItem = ({ exp, stay, index, onChange }) => {
  const [open, setOpen] = useState(null);
  const imgs = stay.images.map(imageUrl);
  const line = specLine(exp.type, stay.specs, stay.route);
  return (
    <li className={`surface overflow-hidden ${stay.chosen ? "ring-2 ring-[hsl(var(--gold))]" : ""}`} data-testid={`stay-item-${index}`}>
      {imgs.length > 0 && (
        <div className="flex snap-x gap-2 overflow-x-auto p-2">
          {imgs.map((src, i) => (
            <button key={src} type="button" onClick={() => setOpen(i)} className="aspect-[4/3] w-[220px] shrink-0 cursor-zoom-in snap-start overflow-hidden rounded-lg" aria-label={`Open ${stay.title} photo ${i + 1}`} data-testid={`stay-${index}-photo-${i}`}>
              <img src={src} alt="" loading="lazy" className="img-cover transition-transform duration-500 hover:scale-[1.03]" />
            </button>
          ))}
        </div>
      )}
      <div className="space-y-3 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-display text-2xl">{stay.title}</h3>
          {stay.chosen && <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--gold))]/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--gold))]" data-testid={`stay-${index}-chosen-badge`}><Crown className="h-3.5 w-3.5" /> Chosen</span>}
        </div>
        {line && <p className="text-sm font-semibold tracking-wide text-[hsl(var(--gold))]" data-testid={`stay-${index}-specs`}>{line}</p>}
        {exp.type === "jet" && <RouteMap points={routeOf(stay)} testId={`stay-${index}-route-map`} />}
        {stay.description && <p className="whitespace-pre-line text-sm text-foreground/85">{stay.description}</p>}
        {stay.url && <a href={stay.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex min-h-[44px] items-center gap-2 text-sm text-primary hover:underline" data-testid={`stay-${index}-link`}>
          View listing{host(stay.url) ? ` on ${host(stay.url)}` : ""} <ExternalLink className="h-3.5 w-3.5" /></a>}
        <VoteBar exp={exp} stay={stay} index={index} onChange={onChange} />
      </div>
      {open !== null && <PhotoViewer photos={imgs} paths={stay.images} index={open} onIndex={setOpen} onClose={() => setOpen(null)} alt={stay.title} captions={exp.captions} />}
    </li>
  );
};

export const StaysList = ({ exp, onChange }) => !exp.stays?.length ? null : (
  <div className="mt-10" data-testid="experience-stays">
    <p className="eyebrow">{typeContent(exp.type).listing.eyebrow}</p>
    {["lead", "cohost"].includes(exp.my_role) && exp.stays.length > 1 && <p className="mt-2 text-sm text-muted-foreground">Vote for your favourite. Only the co-host circle sees the votes.</p>}
    <ul className="mt-4 grid gap-5">{exp.stays.map((s, i) => <StayItem key={s.id || i} exp={exp} stay={s} index={i} onChange={onChange} />)}</ul>
  </div>
);
